// Smart Check Ignored Issues Service - Firebase/API Backend
import type { AxiosInstance } from 'axios';
import { environment } from '../config/environment';

export interface IgnoredIssue {
    id: number | string;
    type: 'costBasis' | 'missingAssetValue';
    currency: string;
    timestamp: number;
}

export interface IgnoredIssuesData {
    [issueId: string]: IgnoredIssue;
}

export interface IgnoredIssuesCount {
    costBasis: number;
    missingAssetValue: number;
    total: number;
}

export class IgnoredIssuesService {
    private apiClient: AxiosInstance;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    /**
     * Get all ignored issues for a specific tax year
     */
    async getIgnoredIssues(taxYear?: string | number): Promise<IgnoredIssuesData> {
        try {
            const params = taxYear ? { taxYear: taxYear.toString() } : {};
            const response = await this.apiClient.get(`${environment.expressApiUrl}/api/smart-check/ignored-issues`, { params });
            return response.data || {};
        } catch (error) {
            console.error('Error fetching ignored issues:', error);
            throw new Error('Failed to fetch ignored issues');
        }
    }

    /**
     * Check if a specific issue is ignored
     */
    async isIssueIgnored(issueId: string | number, taxYear: string | number): Promise<boolean> {
        try {
            const response = await this.apiClient.get(`${environment.expressApiUrl}/api/smart-check/ignored-issues/${issueId}/${taxYear}`);
            return response.data.isIgnored || false;
        } catch (error) {
            console.error('Error checking ignored status:', error);
            return false;
        }
    }

    /**
     * Ignore a specific issue
     */
    async ignoreIssue(
        issueId: string | number,
        taxYear: string | number,
        issueType: 'costBasis' | 'missingAssetValue',
        currency: string
    ): Promise<void> {
        try {
            await this.apiClient.post(`${environment.expressApiUrl}/api/smart-check/ignored-issues`, {
                issueId: issueId.toString(),
                taxYear: taxYear.toString(),
                issueType,
                currency
            });
            console.log(`[IgnoredIssuesService] Ignored ${issueType} issue ${issueId} (${currency})`);
        } catch (error) {
            console.error('Error ignoring issue:', error);
            throw new Error('Failed to ignore issue');
        }
    }

    /**
     * Unignore a specific issue
     */
    async unignoreIssue(issueId: string | number, taxYear: string | number): Promise<void> {
        try {
            await this.apiClient.delete(`${environment.expressApiUrl}/api/smart-check/ignored-issues/${issueId}/${taxYear}`);
            console.log(`[IgnoredIssuesService] Unignored issue ${issueId}`);
        } catch (error) {
            console.error('Error unignoring issue:', error);
            throw new Error('Failed to unignore issue');
        }
    }

    /**
     * Get count of ignored issues by type
     */
    async getIgnoredIssuesCount(taxYear?: string | number): Promise<IgnoredIssuesCount> {
        try {
            const params = taxYear ? { taxYear: taxYear.toString() } : {};
            const response = await this.apiClient.get(`${environment.expressApiUrl}/api/smart-check/ignored-issues/count`, { params });
            return response.data || { costBasis: 0, missingAssetValue: 0, total: 0 };
        } catch (error) {
            console.error('Error getting ignored issues count:', error);
            throw new Error('Failed to get ignored issues count');
        }
    }
}

// Hook to get the ignored issues service instance
export const useIgnoredIssuesService = (apiClient: AxiosInstance | null): IgnoredIssuesService | null => {
    if (!apiClient) return null;
    return new IgnoredIssuesService(apiClient);
};